import { zodResolver } from '@hookform/resolvers/zod'
import clsx from 'clsx'
import { useState, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import type { AuthPasswordChangeModalProps, PasswordChangeFormVO } from './type'
import type { SubmitHandler } from 'react-hook-form'

import {
  Dialog,
  Button,
  FormFieldPassword,
  ErrorMessageList,
} from '@/components/ui'
import { NOTIFICATION_MESSAGE } from '@/const'
import { useChangePassword } from '@/hooks/features/auth'
import { useNotification } from '@/hooks/util'
import { getErrorMessages } from '@/utils/text'

import { schema, formLabels, defaultValues } from './const'

export const AuthPasswordChangeModal = ({
  isOpen,
  onClose,
}: AuthPasswordChangeModalProps) => {
  const { t } = useTranslation()
  const { notify } = useNotification()

  const [errorMessages, setErrorMessages] = useState<string[]>([])

  const { mutate, isLoading } = useChangePassword({})

  const { control, formState, handleSubmit, reset } =
    useForm<PasswordChangeFormVO>({
      resolver: schema && zodResolver(schema),
      defaultValues,
    })

  const submitHandler: SubmitHandler<PasswordChangeFormVO> = useCallback(
    ({ currentPassword, newPassword }) => {
      setErrorMessages([])
      mutate(
        { data: { currentPassword, newPassword } },
        {
          onSuccess: () => {
            notify({
              id: 'NOTIFICATION_MESSAGE.AUTH.UPDATE_PASSWORD',
              type: 'success',
              message: NOTIFICATION_MESSAGE.AUTH.UPDATE_PASSWORD,
            })
            reset()
            onClose()
          },
          onError: (error) => {
            if (error.response?.status === 400) {
              setErrorMessages(
                getErrorMessages({
                  fields: error.response.data.fields,
                  fieldNames: formLabels,
                })
              )
            }
          },
        }
      )
    },
    [mutate, notify, reset, onClose]
  )

  return (
    <Dialog title={t('auth.password.title')} isOpen={isOpen}>
      <div className={clsx('tw-w-full tw-px-2 tw-pb-2')}>
        {errorMessages.length > 0 && (
          <div className={clsx('tw-mt-4')}>
            <ErrorMessageList errorMessages={errorMessages} />
          </div>
        )}

        <form
          onSubmit={handleSubmit(submitHandler)}
          id="password-change-form"
          className={clsx('tw-mt-20')}
        >
          <FormFieldPassword
            label={t('auth.password.currentPassword')}
            required
            error={formState.errors['currentPassword']}
            control={control}
            name="currentPassword"
          />

          <FormFieldPassword
            label={t('auth.password.newPassword')}
            required
            error={formState.errors['newPassword']}
            control={control}
            name="newPassword"
          />

          <FormFieldPassword
            label={t('auth.password.newPasswordConfirm')}
            required
            error={formState.errors['newPasswordConfirm']}
            control={control}
            name="newPasswordConfirm"
          />

          <div
            className={clsx('tw-mt-12 tw-flex tw-items-center tw-space-x-3')}
          >
            <Button
              type="button"
              variant="normal"
              disabled={isLoading}
              onClick={onClose}
            >
              {t('auth.password.close')}
            </Button>
            <Button type="submit" isLoading={isLoading}>
              {t('auth.password.submit')}
            </Button>
          </div>
        </form>
      </div>
    </Dialog>
  )
}
